import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Plus, Edit, Trash2, Search, Loader2, AlertCircle, BookOpen, Tag, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { SuggestionFormModal } from './SuggestionFormModal';
import {
  createSuggestion,
  updateSuggestion,
  deleteSuggestion,
  fetchSuggestions,
} from '../../../api/condition-suggestions.api';
import type {
  ConditionSuggestion,
  ConditionSuggestionCategory,
  ToolScope,
} from '../../../api/condition-suggestions.api';
import { ConditionSuggestionKind } from '../../../api/condition-suggestions.api';

// ─── Constants ────────────────────────────────────────────────────────────────

const TOOL_SCOPE_LABELS: Record<ToolScope | 'all', string> = {
  warm_transfer: 'Warm Transfer',
  outcall: 'Outcall',
  all: 'Alle',
};

const TOOL_SCOPE_BADGE_COLORS: Record<ToolScope, string> = {
  warm_transfer: 'bg-purple-100 text-purple-800',
  outcall: 'bg-orange-100 text-orange-800',
  all: 'bg-gray-100 text-gray-700',
};

/**
 * Human-readable title labels per suggestion kind.
 * Keys map to i18n identifiers (condition_suggestions.tabs.*) — Agent 3 will
 * wire these into the locale files once a translation layer is introduced.
 */
const KIND_TITLE_LABELS: Record<ConditionSuggestionKind, string> = {
  [ConditionSuggestionKind.PRE_CONDITION]: 'Pre-Condition Vorschläge',
  [ConditionSuggestionKind.BEHAVIOR_INSTRUCTION]: 'Behavior-Instruction Vorschläge',
};

// ─── Component ────────────────────────────────────────────────────────────────

interface SuggestionsTabProps {
  /**
   * Determines which suggestion library list to display and filter.
   * PRE_CONDITION = Gate-Bedingungen; BEHAVIOR_INSTRUCTION = Verhaltensregeln.
   * Passed down from ConditionSuggestionsPage based on the active tab.
   */
  kind: ConditionSuggestionKind;
  /** Available categories for filter dropdown and suggestion form. */
  categories: ConditionSuggestionCategory[];
  /** Called after any mutation so parent can refresh categories if needed. */
  onRefresh?: () => void;
  /**
   * Initial category filter — set when the user navigated here by clicking
   * a category row in CategoriesTab. Empty string = no preset filter.
   * Re-syncing whenever this prop changes lets multiple drill-downs in a row work.
   */
  initialCategoryId?: string;
  /**
   * Called when the user wants to leave the suggestion list and return to
   * the categories overview. Required when the parent uses Master-Detail
   * layout (no tabs).
   */
  onBack?: () => void;
}

/**
 * Displays the CRUD list for one suggestion kind (PRE_CONDITION or
 * BEHAVIOR_INSTRUCTION). Fetches its own data using the kind prop so each tab
 * instance is fully self-contained and re-fetches independently.
 */
export function SuggestionsTab({
  kind,
  categories,
  onRefresh,
  initialCategoryId,
  onBack,
}: SuggestionsTabProps) {
  // ─── Data loading ───────────────────────────────────────────────────────────
  const [suggestions, setSuggestions] = useState<ConditionSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchTick, setFetchTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchSuggestions({ kind })
      .then((data) => {
        if (!cancelled) {
          setSuggestions(data);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // fetchTick triggers manual refresh; kind re-fetches when tab changes
  }, [kind, fetchTick]);

  const handleRefresh = () => {
    setFetchTick((t) => t + 1);
    onRefresh?.();
  };

  // ─── Filter state ───────────────────────────────────────────────────────────
  const [filterToolScope, setFilterToolScope] = useState<ToolScope | 'all'>('all');
  const [filterCategoryId, setFilterCategoryId] = useState<string>(initialCategoryId ?? '');

  // Keep filter in sync if the parent navigates here with a new category preset.
  useEffect(() => {
    if (initialCategoryId !== undefined) {
      setFilterCategoryId(initialCategoryId);
    }
  }, [initialCategoryId]);
  const [filterActiveOnly, setFilterActiveOnly] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');

  // ─── Modal / delete state ───────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSuggestion, setEditingSuggestion] = useState<ConditionSuggestion | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ─── Client-side filtering ──────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return suggestions.filter((s) => {
      if (filterToolScope !== 'all' && s.toolScope !== filterToolScope) return false;
      if (filterCategoryId && s.categoryId !== filterCategoryId) return false;
      if (filterActiveOnly && !s.isActive) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const categoryName = s.category?.name?.toLowerCase() ?? '';
        if (!s.text.toLowerCase().includes(term) && !categoryName.includes(term)) return false;
      }
      return true;
    });
  }, [suggestions, filterToolScope, filterCategoryId, filterActiveOnly, searchTerm]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleCreate = () => {
    setEditingSuggestion(null);
    setIsModalOpen(true);
  };

  const handleEdit = (s: ConditionSuggestion) => {
    setEditingSuggestion(s);
    setIsModalOpen(true);
  };

  const handleDeleteRequest = (id: string) => {
    setDeletingId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      await deleteSuggestion(deletingId);
      toast.success('Vorschlag gelöscht.');
      handleRefresh();
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      toast.error(apiError.message ?? 'Fehler beim Löschen des Vorschlags.');
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
    }
  };

  const getCategoryName = (s: ConditionSuggestion): string =>
    s.category?.name ?? categories.find((c) => c.id === s.categoryId)?.name ?? s.categoryId;

  // ─── Render states ──────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        <span className="ml-3 text-gray-500">Lade Vorschläge...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error.message}</p>
          <Button onClick={handleRefresh}>Erneut versuchen</Button>
        </CardContent>
      </Card>
    );
  }

  const activeCategory = filterCategoryId
    ? categories.find((c) => c.id === filterCategoryId)
    : undefined;

  /** i18n-key: condition_suggestions.tabs.pre_conditions / .behavior_instructions */
  const pageTitle = KIND_TITLE_LABELS[kind];

  return (
    <div className="space-y-4">
      {/* Section title — dynamic per kind (i18n-Key: condition_suggestions.tabs.*) */}
      <h2 className="text-lg font-semibold text-gray-800">{pageTitle}</h2>

      {/* Kategorie-Drilldown-Banner: zeigt die aktuell gewählte Kategorie und
          erlaubt die Rückkehr zur Kategorien-Übersicht (Master-Detail-Layout
          ohne Tabs). */}
      {activeCategory && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5">
          <div className="flex items-center gap-2 text-sm text-indigo-900">
            <Tag className="h-4 w-4 text-indigo-600" />
            <span>
              Vorschläge der Kategorie{' '}
              <span className="font-semibold">{activeCategory.name}</span>
            </span>
          </div>
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-indigo-700 hover:text-indigo-900 hover:bg-indigo-100"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Zurück zu den Kategorien
            </Button>
          )}
        </div>
      )}

      {/* Filter bar (Tool-Typ, Aktiv-Toggle, Suche) — Kategorie-Filter
          wird ausschließlich über den Kategorien-Tab-Drilldown gesetzt. */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Tool scope filter */}
        <Select
          value={filterToolScope}
          onValueChange={(v) => setFilterToolScope(v as ToolScope | 'all')}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Tool-Typ..." />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(TOOL_SCOPE_LABELS) as [ToolScope | 'all', string][]).map(
              ([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>

        {/* Active-only toggle */}
        <div className="flex items-center gap-2">
          <Switch
            id="filter-active"
            checked={filterActiveOnly}
            onCheckedChange={setFilterActiveOnly}
          />
          <Label htmlFor="filter-active" className="text-sm cursor-pointer whitespace-nowrap">
            Nur aktive
          </Label>
        </div>

        {/* Search */}
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Text oder Kategorie suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* New button — opens modal pre-loaded with this tab's kind */}
        <Button onClick={handleCreate} size="sm" className="ml-auto">
          <Plus className="w-4 h-4 mr-2" />
          Neuer Vorschlag
        </Button>
      </div>

      {/* Result count */}
      <p className="text-sm text-gray-500">
        {filtered.length} von {suggestions.length} Vorschläge{suggestions.length !== 1 ? 'n' : ''}
      </p>

      {/* Table */}
      {suggestions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">Noch keine Vorschläge vorhanden.</p>
            <Button onClick={handleCreate}>Ersten Vorschlag erstellen</Button>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">
              Keine Vorschläge für die gewählten Filter gefunden.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vorschlagstext</TableHead>
                <TableHead className="w-36">Tool-Typ</TableHead>
                <TableHead className="w-44">Kategorie</TableHead>
                <TableHead className="w-20 text-center">Aktiv</TableHead>
                <TableHead className="w-24 text-right">Reihenfolge</TableHead>
                <TableHead className="w-24 text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.id} className={!s.isActive ? 'opacity-50' : undefined}>
                  <TableCell className="max-w-xs">
                    <p className="text-sm text-gray-900 line-clamp-2">{s.text}</p>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        TOOL_SCOPE_BADGE_COLORS[s.toolScope]
                      }`}
                    >
                      {TOOL_SCOPE_LABELS[s.toolScope]}
                    </span>
                  </TableCell>
                  <TableCell className="text-gray-600 text-sm">
                    {getCategoryName(s)}
                  </TableCell>
                  <TableCell className="text-center">
                    {s.isActive ? (
                      <Badge
                        variant="outline"
                        className="text-xs text-green-700 border-green-300 bg-green-50"
                      >
                        Aktiv
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Inaktiv
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-gray-500 text-sm">
                    {s.displayOrder}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(s)}
                        title="Bearbeiten"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRequest(s.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / Edit Modal — kind is always the tab's kind; in edit mode the
          modal reads kind from the loaded record (read-only). */}
      <SuggestionFormModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editingSuggestion={editingSuggestion}
        kind={editingSuggestion?.kind ?? kind}
        categories={categories}
        onSaved={handleRefresh}
        onCreate={createSuggestion}
        onUpdate={updateSuggestion}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={deletingId !== null}
        onOpenChange={(open) => { if (!open) setDeletingId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vorschlag löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dieser Bedingungs-Vorschlag wird dauerhaft entfernt und kann nicht
              wiederhergestellt werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Löschen...
                </>
              ) : (
                'Löschen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
